import { createAccountMaps } from "./utils";

type Mapping = {
  amount: string;
  stat: string;
  income?: string;
};

type Head = {
  Year?: number;
  Month?: number;
  QtrName?: string;
  CorpId?: string;
};

type AccountLeaf = {
  amount: object | null;
  stat: object | null;
};

type AccountMap = Map<string | number | undefined, AccountMap>;

type ReportConfigItem = {
  response: string;
  incomeResponse: string;
  mappings: Record<string, Mapping>;
  getAccountMaps: (linesInfo: unknown[]) => AccountMap;
  getAccountMap: (map: AccountMap, head: Head) => unknown;
  matchCondition: (itemHead: Head, targetHead: Head) => boolean;
};

type ReportConfig = Record<number, ReportConfigItem>;

export const reportConfig: ReportConfig = {
  1: {
    response: "TotalResponse",
    incomeResponse: "TotalIncomeResponse",
    mappings: {
      Current: {
        amount: "CurrentAmt",
        stat: "CurrentStats",
        income: "CurrentBalance",
      },
      CurrentLY: {
        amount: "LYCurrentAmt",
        stat: "LYCurrentStats",
        income: "LYCurrentBalance",
      },
      CurrentBudget: {
        amount: "CurrentBud",
        stat: "CurrentBudStats",
        income: "CurrentBudget",
      },
      CurrentForecast: {
        amount: "CurrentForecast",
        stat: "CurrentForecastStats",
        income: "CurrentForecast",
      },

      PTD: {
        amount: "PTDAmt",
        stat: "PTDStats",
        income: "PTDBalance",
      },
      PTDLY: {
        amount: "LYPTDAmt",
        stat: "LYPTDStats",
        income: "LYPTDBalance",
      },
      PTDBudget: {
        amount: "PTDBud",
        stat: "PTDBudStats",
        income: "PTDBudget",
      },
      PTDForecast: {
        amount: "PTDForecast",
        stat: "PTDForecastStats",
        income: "PTDForecast",
      },

      QTD: {
        amount: "QTDAmt",
        stat: "QTDStats",
        income: "QTDBalance",
      },
      QTDLY: {
        amount: "LYQTDAmt",
        stat: "LYQTDStats",
        income: "LYQTDBalance",
      },
      QTDBudget: {
        amount: "QTDBud",
        stat: "QTDBudStats",
        income: "QTDBudget",
      },
      QTDForecast: {
        amount: "QTDForecast",
        stat: "QTDForecastStats",
        income: "QTDForecast",
      },

      YTD: {
        amount: "YTDAmt",
        stat: "YTDStats",
        income: "YTDBalance",
      },
      YTDLY: {
        amount: "LYYTDAmt",
        stat: "LYYTDStats",
        income: "LYYTD",
      },
      YTDBudget: {
        amount: "YTDBud",
        stat: "YTDBudStats",
        income: "YTDBudget",
      },
      YTDForecast: {
        amount: "YTDForecast",
        stat: "YTDForecastStats",
        income: "YTDForecast",
      },
    },
    getAccountMaps: (linesInfo) => createAccountMaps(linesInfo),
    getAccountMap: (map) => map,
    matchCondition: () => true,
  },

  2: {
    response: "MonthlyResponse",
    incomeResponse: "MonthlyIncomeResponse",
    mappings: {
      Month: { amount: "MTDAmt", stat: "MTDStats" },
      MonthLY: { amount: "LYMTDAmt", stat: "LYMTDStats" },
      MonthBudget: { amount: "MTDBud", stat: "MTDBudStats" },
      MonthForecast: {
        amount: "MTDForecast",
        stat: "MTDForecastStats",
      },
    },
    getAccountMaps: (linesInfo) =>
      createAccountMaps(linesInfo, "Year", "Month"),
    getAccountMap: (map, { Year, Month }) => map.get(Year)?.get(Month),
    matchCondition: (i, { Year, Month }) =>
      i.Year === Year && i.Month === Month,
  },

  3: {
    response: "QuarterlyResponse",
    incomeResponse: "QuarterlyIncomeResponse",
    mappings: {
      Quarter: { amount: "QTDAmt", stat: "QTDStats" },
      QuarterLY: { amount: "LYQTDAmt", stat: "LYQTDStats" },
      QuarterBudget: { amount: "QTDBud", stat: "QTDBudStats" },
      QuarterForecast: {
        amount: "QTDForecast",
        stat: "QTDForecastStats",
      },
    },
    getAccountMaps: (linesInfo) =>
      createAccountMaps(linesInfo, "Year", "QtrName"),
    getAccountMap: (map, { Year, QtrName }) => map.get(Year)?.get(QtrName),
    matchCondition: (i, { Year, QtrName }) =>
      i.Year === Year && i.QtrName === QtrName,
  },

  4: {
    response: "YearlyResponse",
    incomeResponse: "YearlyIncomeResponse",
    mappings: {
      Yearly: { amount: "YTDAmt", stat: "YTDStats" },
      YearlyLY: { amount: "LYYTDAmt", stat: "LYYTDStats" },
      YearlyBudget: { amount: "YTDBud", stat: "YTDBudStats" },
      YearlyForecast: {
        amount: "YTDForecast",
        stat: "YTDForecastStats",
      },
    },
    getAccountMaps: (linesInfo) => createAccountMaps(linesInfo, "Year"),
    getAccountMap: (map, { Year }) => map.get(Year),
    matchCondition: (i, { Year }) => i.Year === Year,
  },

  5: {
    response: "DynamicMonthlyResponse",
    incomeResponse: "DynamicMonthlyIncomeResponse",
    mappings: {
      DynamicMonth: { amount: "MTDAmt", stat: "MTDStats" },
      DynamicMonthLY: { amount: "LYMTDAmt", stat: "LYMTDStats" },
      DynamicMonthBudget: { amount: "MTDBud", stat: "MTDBudStats" },
      DynamicMonthForecast: {
        amount: "MTDForecast",
        stat: "MTDForecastStats",
      },
    },
    getAccountMaps: (linesInfo) =>
      createAccountMaps(linesInfo, "Year", "Month"),
    getAccountMap: (map, { Year, Month }) => map.get(Year)?.get(Month),
    matchCondition: (i, { Year, Month }) =>
      i.Year === Year && i.Month === Month,
  },

  6: {
    response: "CombinedReportResponse",
    incomeResponse: "CombinedIncomeResponse",
    mappings: {
      Current: { amount: "CurrentAmt", stat: "CurrentStats" },
      CurrentLY: { amount: "LYCurrentAmt", stat: "LYCurrentStats" },
      CurrentBudget: { amount: "CurrentBud", stat: "CurrentBudStats" },
      CurrentForecast: {
        amount: "CurrentForecast",
        stat: "CurrentForecastStats",
      },
    },
    getAccountMaps: (linesInfo) =>
      createAccountMaps(linesInfo, "Corporationid"),
    getAccountMap: (map, { CorpId }) => map.get(CorpId),
    matchCondition: (i, { CorpId }) => i.CorpId === CorpId,
  },
};
